import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import Chart from "chart.js/auto";

// Layout & UI
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { TextField, useTheme, Typography } from "@mui/material";

// Date pickers
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// Custom card component
import FuelLogCards from "./fuelLogCards";
import { useMaterialUIController } from "context";

const BASE = "https://v3.dhundhoo.com/fuel/log";

const ENDPOINTS = {
  vendorPerformance: `${BASE}/vendor-performance`,
  projectCost: `${BASE}/project-cost`,
  pendingBills: `${BASE}/pending-bills`,
  paymentMode: `${BASE}/payment-mode`,
  fuelCost: `${BASE}/fuel-cost`,
  fuelConsumption: `${BASE}/fuel-consumption`,
  driverEfficiency: `${BASE}/driver-efficiency`,
  costPerKm: `${BASE}/cost-per-km`,
};

function FuelLogAnalytics() {
  const [startDate, setStartDate] = useState(new Date("2025-11-01"));
  const [endDate, setEndDate] = useState(new Date("2025-11-30"));

  const [vendorPerf, setVendorPerf] = useState(null);
  const [projectCost, setProjectCost] = useState(null);
  const [pendingBills, setPendingBills] = useState(null);
  const [paymentMode, setPaymentMode] = useState(null);
  const [fuelCost, setFuelCost] = useState(null);
  const [fuelCons, setFuelCons] = useState(null);
  const [driverEff, setDriverEff] = useState(null);
  const [costPerKm, setCostPerKm] = useState(null);

  const [loading, setLoading] = useState({});

  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();

  const refs = {
    vendorPie: useRef(null),
    projectBar: useRef(null),
    pendingDoughnut: useRef(null),
    paymentPie: useRef(null),
    fuelLine: useRef(null),
    consBar: useRef(null),
    driverBar: useRef(null),
    costKmBar: useRef(null),
  };
  const chartInstances = useRef({});

  const token = localStorage.getItem("token");
  const headers = { "x-dhundhoo-session": token };

  const iso = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}T00:00:00`;

  const get = async (url, setter, key) => {
    setLoading((p) => ({ ...p, [key]: true }));
    try {
      const res = await axios.get(url, { headers });
      setter(res.data.data);
    } catch (e) {
      console.error(key, e);
    } finally {
      setLoading((p) => ({ ...p, [key]: false }));
    }
  };

  const fetchAll = useCallback(() => {
    const s = iso(startDate);
    const e = iso(endDate);
    const qs = `?start=${s}&end=${e}`;

    get(`${ENDPOINTS.vendorPerformance}${qs}`, setVendorPerf, "vendorPerf");
    get(`${ENDPOINTS.projectCost}${qs}`, setProjectCost, "projectCost");
    get(ENDPOINTS.pendingBills, setPendingBills, "pendingBills");
    get(`${ENDPOINTS.paymentMode}${qs}`, setPaymentMode, "paymentMode");
    get(`${ENDPOINTS.fuelCost}${qs}`, setFuelCost, "fuelCost");
    get(`${ENDPOINTS.fuelConsumption}${qs}`, setFuelCons, "fuelCons");
    get(`${ENDPOINTS.driverEfficiency}${qs}`, setDriverEff, "driverEff");
    get(`${ENDPOINTS.costPerKm}${qs}`, setCostPerKm, "costPerKm");
  }, [startDate, endDate]);

  useEffect(() => fetchAll(), [fetchAll]);

  const textColor = darkMode ? "#E0E0E0" : "#111";
  const gridColor = darkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";

  const baseOpts = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { labels: { color: textColor } },
      title: { display: false },
    },
    scales: {
      x: { ticks: { color: textColor }, grid: { color: gridColor } },
      y: { ticks: { color: textColor }, grid: { color: gridColor } },
    },
  };

  const destroy = (key) => {
    if (chartInstances.current[key]) {
      chartInstances.current[key].destroy();
      chartInstances.current[key] = null;
    }
  };

  // 1. Vendor Performance – Pie (Chart on Top)
  useEffect(() => {
    if (!vendorPerf || !refs.vendorPie.current) return;
    destroy("vendorPie");
    const labels = Object.keys(vendorPerf.vendorPerformance);
    const data = labels.map((k) => vendorPerf.vendorPerformance[k].totalAmount);
    chartInstances.current.vendorPie = new Chart(refs.vendorPie.current, {
      type: "pie",
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6"],
          borderWidth: 2,
          borderColor: darkMode ? "#222" : "#fff",
        }],
      },
      options: { ...baseOpts },
    });
  }, [vendorPerf, darkMode]);

  // 4. Payment Mode – Pie (Chart on Top)
  useEffect(() => {
    if (!paymentMode || !refs.paymentPie.current) return;
    destroy("paymentPie");
    const labels = Object.keys(paymentMode.paymentModeSummary);
    const data = Object.values(paymentMode.paymentModeSummary);
    chartInstances.current.paymentPie = new Chart(refs.paymentPie.current, {
      type: "pie",
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: ["#10b981", "#f59e0b", "#3b82f6"],
          borderWidth: 2,
          borderColor: darkMode ? "#222" : "#fff",
        }],
      },
      options: { ...baseOpts },
    });
  }, [paymentMode, darkMode]);

  // Other charts (unchanged logic, just rendering)
  useEffect(() => {
    if (!projectCost || !refs.projectBar.current) return;
    destroy("projectBar");
    const labels = Object.keys(projectCost.projectCosts);
    const data = labels.map((k) => projectCost.projectCosts[k].totalAmount);
    chartInstances.current.projectBar = new Chart(refs.projectBar.current, {
      type: "bar",
      data: { labels, datasets: [{ label: "₹", data, backgroundColor: "#6366f1", borderRadius: 6 }] },
      options: {
        ...baseOpts,
        plugins: { ...baseOpts.plugins, title: { display: true, text: "Project Fuel Cost", color: textColor } },
        scales: { y: { ...baseOpts.scales.y, ticks: { ...baseOpts.scales.y.ticks, callback: (v) => `₹${v}` } } },
      },
    });
  }, [projectCost, darkMode]);

  useEffect(() => {
    if (!pendingBills || !refs.pendingDoughnut.current) return;
    destroy("pendingDoughnut");
    const labels = Object.keys(pendingBills.pendingAmountByVendor);
    const data = Object.values(pendingBills.pendingAmountByVendor);
    chartInstances.current.pendingDoughnut = new Chart(refs.pendingDoughnut.current, {
      type: "doughnut",
      data: { labels, datasets: [{ data, backgroundColor: ["#ef4444", "#f59e0b", "#10b981", "#3b82f6", "#8b5cf6"] }] },
      options: {
        ...baseOpts,
        plugins: { ...baseOpts.plugins, title: { display: true, text: "Pending Bills", color: textColor } },
      },
    });
  }, [pendingBills, darkMode]);

  useEffect(() => {
    if (!fuelCost || !refs.fuelLine.current) return;
    destroy("fuelLine");
    chartInstances.current.fuelLine = new Chart(refs.fuelLine.current, {
      type: "line",
      data: {
        labels: ["Start", "End"],
        datasets: [{ label: "₹", data: [fuelCost.totalAmount, fuelCost.totalAmount], borderColor: "#6366f1", tension: 0.3 }],
      },
      options: {
        ...baseOpts,
        plugins: { ...baseOpts.plugins, title: { display: true, text: "Fuel Cost (Period)", color: textColor } },
        scales: { y: { ...baseOpts.scales.y, ticks: { ...baseOpts.scales.y.ticks, callback: (v) => `₹${v}` } } },
      },
    });
  }, [fuelCost, darkMode]);

  useEffect(() => {
    if (!fuelCons || !refs.consBar.current) return;
    destroy("consBar");
    const labels = Object.keys(fuelCons.fuelConsumption);
    const data = Object.values(fuelCons.fuelConsumption);
    chartInstances.current.consBar = new Chart(refs.consBar.current, {
      type: "bar",
      data: { labels, datasets: [{ label: "Litres", data, backgroundColor: "#10b981", borderRadius: 6 }] },
      options: {
        ...baseOpts,
        plugins: { ...baseOpts.plugins, title: { display: true, text: "Fuel Consumption", color: textColor } },
        scales: { y: { ...baseOpts.scales.y, ticks: { ...baseOpts.scales.y.ticks, callback: (v) => `${v} L` } } },
      },
    });
  }, [fuelCons, darkMode]);

  useEffect(() => {
    if (!driverEff || !refs.driverBar.current) return;
    destroy("driverBar");
    const labels = Object.keys(driverEff.driverEfficiency);
    const mileage = labels.map((k) => driverEff.driverEfficiency[k].avgMileage);
    const costKm = labels.map((k) => driverEff.driverEfficiency[k].avgCostPerKm);
    chartInstances.current.driverBar = new Chart(refs.driverBar.current, {
      type: "bar",
      data: {
        labels,
        datasets: [
          { label: "Mileage (km/L)", data: mileage, backgroundColor: "#3b82f6" },
          { label: "Cost/km (₹)", data: costKm, backgroundColor: "#f59e0b" },
        ],
      },
      options: {
        ...baseOpts,
        indexAxis: "y",
        plugins: { ...baseOpts.plugins, title: { display: true, text: "Driver Efficiency", color: textColor } },
      },
    });
  }, [driverEff, darkMode]);

  useEffect(() => {
    if (!costPerKm || !refs.costKmBar.current) return;
    destroy("costKmBar");
    const labels = Object.keys(costPerKm.costPerKmByVehicle);
    const data = Object.values(costPerKm.costPerKmByVehicle);
    chartInstances.current.costKmBar = new Chart(refs.costKmBar.current, {
      type: "bar",
      data: { labels, datasets: [{ label: "₹/km", data, backgroundColor: "#ef4444", borderRadius: 6 }] },
      options: {
        ...baseOpts,
        plugins: { ...baseOpts.plugins, title: { display: true, text: "Cost per KM", color: textColor } },
        scales: { y: { ...baseOpts.scales.y, ticks: { ...baseOpts.scales.y.ticks, callback: (v) => `₹${v}` } } },
      },
    });
  }, [costPerKm, darkMode]);

  const handleApply = () => {
    if (startDate && endDate && startDate <= endDate) fetchAll();
  };

  const cardData = {
    totalAmount: fuelCost?.totalAmount ?? 0,
    totalFuelQty: fuelCost?.totalFuelQty ?? 0,
    averageRatePerLitre: fuelCost?.averageRatePerLitre ?? 0,
    totalPendingBills: pendingBills?.totalPendingBills,
    pendingAmountByVendor: pendingBills?.pendingAmountByVendor,
    paymentModeSummary: paymentMode?.paymentModeSummary,
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox p={3}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2} alignItems="center" mb={4}>
            <Grid item xs={12} sm={5} md={3}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={setStartDate}
                renderInput={(p) => <TextField {...p} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={5} md={3}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={setEndDate}
                renderInput={(p) => <TextField {...p} fullWidth size="small" />}
              />
            </Grid>
            <Grid item xs={12} sm={2} md={2}>
              <MDButton
                fullWidth
                variant="gradient"
                color="warning"
                size="medium"
                onClick={handleApply}
                disabled={Object.values(loading).some((v) => v)}
              >
                {Object.values(loading).some((v) => v) && <CircularProgress size={18} color="inherit" />} APPLY
              </MDButton>
            </Grid>
          </Grid>
        </LocalizationProvider>

        {/* LARGE KPI CARDS */}
        <FuelLogCards data={cardData} />

        <MDBox mt={6}>
          <Typography variant="h5" fontWeight="600" color={textColor} mb={3}>
            Analytics Dashboard
          </Typography>

          <div style={{
            display: "grid",
            gap: "24px",
            gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))",
          }}>
            {/* Vendor Performance - Chart on Top */}
            <ChartBoxWithText
              ref={refs.vendorPie}
              loading={loading.vendorPerf}
              title="Vendor Performance"
              data={vendorPerf?.vendorPerformance}
              renderText={(item) => (
                <div>
                  <Typography variant="subtitle2" fontWeight="bold">{item[0]}</Typography>
                  <Typography variant="body2">₹{item[1].totalAmount.toFixed(2)}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {item[1].transactions} txn • ₹{item[1].averageRatePerLitre.toFixed(2)}/L
                  </Typography>
                </div>
              )}
            />

            {/* Project Cost */}
            <ChartBox ref={refs.projectBar} loading={loading.projectCost} title="Project Cost" />

            {/* Pending Bills */}
            <ChartBox ref={refs.pendingDoughnut} loading={loading.pendingBills} title="Pending Bills" />

            {/* Payment Mode - Chart on Top */}
            <ChartBoxWithText
              ref={refs.paymentPie}
              loading={loading.paymentMode}
              title="Payment Mode"
              data={paymentMode?.paymentModeSummary}
              renderText={(item) => (
                <div>
                  <Typography variant="subtitle2" fontWeight="bold">{item[0]}</Typography>
                  <Typography variant="body2">₹{item[1].toFixed(2)}</Typography>
                </div>
              )}
            />

            {/* Fuel Cost Trend */}
            <ChartBox ref={refs.fuelLine} loading={loading.fuelCost} title="Fuel Cost Trend" />

            {/* Fuel Consumption */}
            <ChartBox ref={refs.consBar} loading={loading.fuelCons} title="Fuel Consumption" />

            {/* Driver Efficiency */}
            <ChartBox ref={refs.driverBar} loading={loading.driverEff} title="Driver Efficiency" />

            {/* Cost per KM */}
            <ChartBox ref={refs.costKmBar} loading={loading.costPerKm} title="Cost per KM" />
          </div>
        </MDBox>
      </MDBox>
    </DashboardLayout>
  );
}

// Reusable Chart Box
const ChartBox = React.forwardRef(({ loading, title }, ref) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const bg = darkMode ? "#1A2035" : "#fff";
  const color = darkMode ? "#E0E0E0" : "#111";

  return (
    <div style={{
      backgroundColor: bg,
      padding: "20px",
      borderRadius: "16px",
      boxShadow: darkMode ? "0 4px 12px rgba(0,0,0,0.6)" : "0 4px 16px rgba(0,0,0,0.1)",
      minHeight: "380px",
      display: "flex",
      flexDirection: "column",
    }}>
      <Typography variant="h6" fontWeight="600" color={color} mb={2}>
        {title}
      </Typography>
      {loading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </MDBox>
      ) : (
        <div style={{ flex: 1, minHeight: 260 }}>
          <canvas ref={ref} />
        </div>
      )}
    </div>
  );
});

// Special Chart Box with Text Below
const ChartBoxWithText = React.forwardRef(({ loading, title, data, renderText }, ref) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const bg = darkMode ? "#1A2035" : "#fff";
  const color = darkMode ? "#E0E0E0" : "#111";
  const entries = data ? Object.entries(data) : [];

  return (
    <div style={{
      backgroundColor: bg,
      padding: "20px",
      borderRadius: "16px",
      boxShadow: darkMode ? "0 4px 12px rgba(0,0,0,0.6)" : "0 4px 16px rgba(0,0,0,0.1)",
      minHeight: "420px",
      display: "flex",
      flexDirection: "column",
    }}>
      <Typography variant="h6" fontWeight="600" color={color} mb={2}>
        {title}
      </Typography>

      {loading ? (
        <MDBox display="flex" justifyContent="center" alignItems="center" flex={1}>
          <CircularProgress />
        </MDBox>
      ) : (
        <>
          <div style={{ flex: 1, minHeight: 220 }}>
            <canvas ref={ref} />
          </div>
          <MDBox mt={2} sx={{ maxHeight: 120, overflowY: "auto" }}>
            {entries.map((item, i) => (
              <MDBox key={i} mb={1.5} p={1.5} bgcolor={darkMode ? "#2d3748" : "#f7fafc"} borderRadius={2}>
                {renderText(item)}
              </MDBox>
            ))}
          </MDBox>
        </>
      )}
    </div>
  );
});

export default FuelLogAnalytics;